import {
  CstNode,
  CstParser,
  IRuleConfig,
  ITokenConfig,
  Lexer,
  TokenType,
  TokenVocabulary,
  createToken as orgCreateToken,
} from 'chevrotain';
import XRegExp from 'xregexp';

/**
 * From https://github.com/SAP/chevrotain/blob/master/examples/grammars/graphql/graphql.js
 *
 * TODOS:
 * - Test when missing an argument, what happens?
 * - Is there another way to get around empty selection set besides modifying the grammar?
 * - I believe we can support these cases by adding 'error but continue' style rules to the grammar... right?
 */

/**
 * GraphQL grammar based on the specification at https://spec.graphql.org/draft
 *
 * The implementations follows the specs as directly as possible.
 * This means:
 * - Same rule names.
 * - Same Token names.
 *
 * Some things had to be refactored, for example to be an LL(K) grammar.
 * Or to extract repeating code in a rule.
 * But the refactored code is equivalent.
 */

// ----------------- lexer -----------------
// A little mini DSL for easier lexer definition using xRegExp.
const fragments: Record<string, RegExp> = {};
export function FRAGMENT(name: string, def: string) {
  fragments[name] = XRegExp.build(def, fragments);
}
export function MAKE_PATTERN(def: string, flags?: string) {
  return XRegExp.build(def, fragments, flags);
}

export const sharedTokens: TokenType[] = [];
export const createToken = (config: ITokenConfig, allTokens = sharedTokens) => {
  const newToken = orgCreateToken.apply(null, [config]);
  allTokens.push(newToken);
  return newToken;
};

export const sharedKeywordTokens: TokenType[] = [];
export function createKeywordToken(
  config: ITokenConfig,
  allTokens = sharedTokens,
  keywordTokens = sharedKeywordTokens,
) {
  // eslint-disable-next-line no-param-reassign
  config.longer_alt = NameButNotKeyword;
  const newToken = createToken(config, allTokens);
  keywordTokens.push(newToken);
  newToken.CATEGORIES = (newToken.CATEGORIES || []).concat(Keyword);
  return newToken;
}

// B1 - Ignored-Tokens:
// https://spec.graphql.org/draft/#sec-Language.Source-Text.Ignored-Tokens

// WhiteSpace: https://spec.graphql.org/draft/#White-Space
createToken({
  name: 'WhiteSpace',
  pattern: /[ \t]+/,
  group: Lexer.SKIPPED,
});

// UnicodeBOM: https://spec.graphql.org/draft/#UnicodeBOM
createToken({
  name: 'UnicodeBOM',
  pattern: '\uFFFE',
  group: Lexer.SKIPPED,
});

// LineTerminator: https://spec.graphql.org/draft/#LineTerminator
createToken({
  name: 'LineTerminator',
  pattern: /\n\r|\r|\n/,
  group: Lexer.SKIPPED,
});

// Comment: https://spec.graphql.org/draft/#Comment
createToken({
  name: 'Comment',
  pattern: /#[^\n\r]*/,
  group: Lexer.SKIPPED,
});

// Comma: https://spec.graphql.org/draft/#Comma
createToken({
  name: 'Comma',
  pattern: ',',
  group: Lexer.SKIPPED,
});

// B2 - Lexical Tokens
// https://spec.graphql.org/draft/#sec-Language.Source-Text.Lexical-Tokens
const Keyword = createToken({ name: 'Keyword', pattern: Lexer.NA });
export const At = createToken({ name: 'At', pattern: '@' });
const Exclamation = createToken({ name: 'Exclamation', pattern: '!' });
export const LParen = createToken({ name: 'LParen', pattern: '(' });
export const RParen = createToken({ name: 'RParen', pattern: ')' });
export const Colon = createToken({ name: 'Colon', pattern: ':' });
export const Equals = createToken({ name: 'Equals', pattern: '=' });
export const LSquare = createToken({ name: 'LSquare', pattern: '[' });
export const RSquare = createToken({ name: 'RSquare', pattern: ']' });
export const LCurly = createToken({ name: 'LCurly', pattern: '{' });
export const RCurly = createToken({ name: 'RCurly', pattern: '}' });

// ##NameButNotKeyword
// Name must not be placed into the TokenTypeList before any keywords
// as it can match any keyword, so we use "orgCreateToken"
export const NameButNotKeyword = orgCreateToken({
  name: 'NameButNotKeyword',
  pattern: /[_A-Za-z][_0-9A-Za-z]*/,
});

// keywords and Name
const Query = createKeywordToken({ name: 'Query', pattern: 'query' });
const Mutation = createKeywordToken({
  name: 'Mutation',
  pattern: 'mutation',
});
const Subscription = createKeywordToken({
  name: 'Subscription',
  pattern: 'subscription',
});
const True = createKeywordToken({ name: 'True', pattern: 'true' });
const False = createKeywordToken({ name: 'False', pattern: 'false' });
const Null = createKeywordToken({ name: 'Null', pattern: 'null' });

FRAGMENT('IntegerPart', '-?(0|[1-9][0-9]*)');
FRAGMENT('FractionalPart', '\\.[0-9]+');
FRAGMENT('ExponentPart', '[eE][+-]?[0-9]+');
export const IntValue = createToken({
  name: 'IntValue',
  pattern: MAKE_PATTERN('{{IntegerPart}}'),
});
export const FloatValue = createToken({
  name: 'FloatValue',
  pattern: MAKE_PATTERN(
    '{{IntegerPart}}{{FractionalPart}}({{ExponentPart}})?|{{IntegerPart}}{{ExponentPart}}',
  ),
});
FRAGMENT('EscapedCharacter', '[\\\\/"bfnrt]');
FRAGMENT('EscapedUnicode', '[0-9a-fA-F]{4}');
FRAGMENT(
  'StringCharacter',
  '(?:[^\\\\"\\n\\r]|\\\\(?:u{{EscapedUnicode}}|{{EscapedCharacter}}))',
);
FRAGMENT('BlockStringCharacter', '\\\\"""|[^"]|"(?!"")');
export const StringValue = createToken({
  name: 'StringValue',
  pattern: MAKE_PATTERN(
    '"""(?:{{BlockStringCharacter}})*"""|"(?:{{StringCharacter}})*"',
  ),
});

export type SharedNodeType =
  | 'Name'
  | 'NameButNotTrueOrFalseOrNull'
  | 'Directives'
  | 'Directive'
  | 'Arguments'
  | 'Argument'
  | 'NamedType'
  | 'BooleanValue'
  | 'NullValue'
  | 'EnumValue'
  | 'ListValue'
  | 'ObjectValue'
  | 'ObjectField'
  | 'OperationType'
  | 'Type'
  | 'DefaultValue'
  | 'ListType';
type CstParserRules = {
  [nodeType in SharedNodeType | 'Value']: (idx?: number) => CstNode;
};

export abstract class SharedParser<NodeType extends string> extends CstParser {
  keywordTokens: TokenType[];

  // #AddRulesToParser
  RULE!: (
    name: NodeType | SharedNodeType,
    implementation: (...implArgs: unknown[]) => unknown,
    config?: IRuleConfig<CstNode>,
  ) => (idx?: number, ...unknown: unknown[]) => CstNode;

  constructor(allTokens: TokenVocabulary, keywordTokens: TokenType[]) {
    super(allTokens, { recoveryEnabled: true, nodeLocationTracking: 'full' });

    this.keywordTokens = keywordTokens;
    this.addSharedRulesToParser();
    this.addRulesToParser();

    // very important to call this after all the rules have been defined.
    // otherwise the parser may not work correctly as it will lack information
    // derived during the self analysis phase.
    this.performSelfAnalysis();
  }

  addSharedRulesToParser() {
    // ##AddRulesToParser TODO (jason) could not get this class to have a union
    // index signature, this should be safe though
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const $: SharedParser<NodeType> & CstParserRules = this as any;

    // Helper node for a Name or any keywordToken
    $.RULE('Name', () => {
      $.OR([
        { ALT: () => $.CONSUME(NameButNotKeyword) },
        ...this.keywordTokens.map((keywordToken) => ({
          ALT: () => $.CONSUME(keywordToken),
        })),
      ]);
    });

    // Not in spec: represents "Name but not true or false or null"
    //
    // The normal 'Name' parse rule allows all keywords, this one special cases
    // 'true', 'false', and 'null' to not be included.
    $.RULE('NameButNotTrueOrFalseOrNull', () => {
      $.OR([
        { ALT: () => $.CONSUME(NameButNotKeyword) },
        ...this.keywordTokens
          .filter((keywordToken) => ![True, False, Null].includes(keywordToken))
          .map((keywordToken) => ({
            ALT: () => $.CONSUME(keywordToken),
          })),
      ]);
    });

    // https://spec.graphql.org/draft/#OperationType
    //   OperationType : one of
    //     query mutation subscription
    $.RULE('OperationType', () => {
      $.OR([
        { ALT: () => $.CONSUME(Query) },
        { ALT: () => $.CONSUME(Mutation) },
        { ALT: () => $.CONSUME(Subscription) },
      ]);
    });

    // https://spec.graphql.org/draft/#Directives
    //   Directives[const] :
    //     Directive[?const][list]
    $.RULE('Directives', (isConst) => {
      $.AT_LEAST_ONE(() => {
        $.SUBRULE($.Directive, { ARGS: [isConst] });
      });
    });

    // https://spec.graphql.org/draft/#Directive
    //   Directive[const] :
    //     @ Name Arguments[?const[opt]
    $.RULE('Directive', (isConst) => {
      $.CONSUME(At);
      $.SUBRULE($.Name);
      $.OPTION(() => {
        $.SUBRULE($.Arguments, { ARGS: [isConst] });
      });
    });

    // https://spec.graphql.org/draft/#Arguments
    //   Arguments[const] :
    //     ( Argument[?const][list] )
    $.RULE('Arguments', (isConst) => {
      $.CONSUME(LParen);
      $.AT_LEAST_ONE(() => {
        $.SUBRULE($.Argument, { ARGS: [isConst] });
      });
      $.CONSUME(RParen);
    });

    // https://spec.graphql.org/draft/#Argument
    //   Argument[const] :
    //     Name : Value[?const]
    $.RULE('Argument', (isConst) => {
      $.SUBRULE($.Name);
      $.CONSUME(Colon);
      $.SUBRULE($.Value, { ARGS: [isConst] });
    });

    // https://spec.graphql.org/draft/#NamedType
    //   NamedType :
    //     Name
    $.RULE('NamedType', () => {
      $.SUBRULE($.Name);
    });

    // https://spec.graphql.org/draft/#BooleanValue
    //   BooleanValue : one of
    //     true false
    $.RULE('BooleanValue', () => {
      $.OR([{ ALT: () => $.CONSUME(True) }, { ALT: () => $.CONSUME(False) }]);
    });

    // https://spec.graphql.org/draft/#NullValue
    //   NullValue :
    //     null
    $.RULE('NullValue', () => {
      $.CONSUME(Null);
    });

    // https://spec.graphql.org/draft/#EnumValue
    //   EnumValue :
    //     Name but not true or false or null
    $.RULE('EnumValue', () => {
      $.SUBRULE($.NameButNotTrueOrFalseOrNull);
    });

    // https://spec.graphql.org/draft/#ListValue
    //   ListValue :
    //     [ ]
    //     [ Value[?const][list] ]
    $.RULE('ListValue', (isConst) => {
      $.CONSUME(LSquare);
      $.MANY(() => {
        $.SUBRULE($.Value, { ARGS: [isConst] });
      });
      $.CONSUME(RSquare);
    });

    // https://spec.graphql.org/draft/#ObjectValue
    //   ObjectValue[const] :
    //     { }
    //     { ObjectField[?const][list] }
    $.RULE('ObjectValue', (isConst) => {
      $.CONSUME(LCurly);
      $.MANY(() => {
        $.SUBRULE($.ObjectField, { ARGS: [isConst] });
      });
      $.CONSUME(RCurly);
    });

    // https://spec.graphql.org/draft/#ObjectField
    //   ObjectField[const] :
    //     Name : Value[?const]
    $.RULE('ObjectField', (isConst) => {
      $.SUBRULE($.Name);
      $.CONSUME(Colon);
      $.SUBRULE($.Value, { ARGS: [isConst] });
    });

    // https://spec.graphql.org/draft/#DefaultValue
    //   DefaultValue :
    //     = Value[const]
    $.RULE('DefaultValue', () => {
      $.CONSUME(Equals);
      $.SUBRULE($.Value, { ARGS: [true] });
    });

    // https://spec.graphql.org/draft/#Type
    //   Type :
    //     NamedType
    //     ListType
    //     NonNullType
    //
    //   NonNullType :
    //     NamedType !
    //     ListType !
    //
    // Remove NonNullType in favor of adding an optional '!'
    $.RULE('Type', () => {
      $.OR([
        { ALT: () => $.SUBRULE($.NamedType) },
        { ALT: () => $.SUBRULE($.ListType) },
      ]);

      $.OPTION(() => {
        $.CONSUME(Exclamation);
      });
    });

    // https://spec.graphql.org/draft/#ListType
    //   ListType :
    //     [ Type ]
    $.RULE('ListType', () => {
      $.CONSUME(LSquare);
      $.SUBRULE($.Type);
      $.CONSUME(RSquare);
    });
  }

  abstract addRulesToParser(): void;
}
